import { auth } from "firebase-config";

// This should match the Organization interface in the backend
export interface Organization {
  id: string;
  name: string;
  address?: string; // Optional address
  contact?: string; // Optional contact (phone/email)
  createdAt: string; // Assuming it comes as an ISO string
  members: string[]; // Array of user uids
  code?: string;
  codeExpiresAt?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getAuthHeader = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

// Strongly-typed in-memory cache and in-flight de-duplication
type OrgsCacheState = {
  data: Organization[] | null;
  expiresAt: number;
  inflight: Promise<Organization[]> | null;
};

const ORGS_TTL_MS = 5 * 60 * 1000; // 5 minutes
const orgsState: OrgsCacheState = { data: null, expiresAt: 0, inflight: null };

export const getOrganizations = async (forceRefresh?: boolean): Promise<Organization[]> => {
  const now = Date.now();
  if (!forceRefresh && orgsState.data && orgsState.expiresAt > now) {
    return orgsState.data;
  }
  if (orgsState.inflight) {
    return orgsState.inflight;
  }

  orgsState.inflight = (async () => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/organizations`, { headers });
    if (!response.ok) {
      throw new Error("Failed to fetch organizations");
    }
    const data = (await response.json()) as Organization[];
    orgsState.data = data;
    orgsState.expiresAt = Date.now() + ORGS_TTL_MS;
    orgsState.inflight = null;
    return data;
  })();

  return orgsState.inflight;
};

export const invalidateOrganizationsCache = () => {
  orgsState.data = null;
  orgsState.expiresAt = 0;
  orgsState.inflight = null;
};

export const createOrganization = async (data: { name: string; [key: string]: any }): Promise<Organization> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/organizations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to create organization');
    }

    return response.json();
}

export const getOrganizationById = async (id: string): Promise<Organization> => {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/v1/organizations/${id}`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch organization ${id}`);
  }

  return response.json();
};

export const regenerateOrganizationCode = async (id: string): Promise<Organization> => {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/v1/organizations/${id}/regenerate-code`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    throw new Error('Failed to regenerate organization code');
  }

  return response.json();
}

export const addMemberToOrganization = async (orgId: string, userId: string): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/organizations/${orgId}/members`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to add member to organization (${response.status})`;
        throw new Error(errorMessage);
    }
};

export const deleteOrganization = async (id: string): Promise<void> => {
    const headers = await getAuthHeader();
    await fetch(`${API_BASE_URL}/api/v1/organizations/${id}`, {
        method: 'DELETE',
        headers,
    });
};

export const removeMemberFromOrganization = async (orgId: string, userId: string): Promise<void> => {
    const headers = await getAuthHeader();
    await fetch(`${API_BASE_URL}/api/v1/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE',
        headers,
    });
}; 